import { useMemo } from 'react';
import { Link } from 'react-router';
import { Eye, PenLine, Signature } from 'lucide-react';
import { clsx } from 'clsx';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';
import { FilterSelect } from '@/components/list/FilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { Panel } from '@/components/Panel';
import { Pagination } from '@/components/table/Pagination';
import { TeamTag } from '@/components/teams/TeamTag';
import { AbsenceBadge } from '@/components/timesheet/AbsenceBadge';
import { AbsencesToSignBox } from '@/components/timesheet/AbsencesToSign';
import { PendingSignatureTag } from '@/components/timesheet/SignatureCell';
import { loadSignatures } from '@/components/timesheet/signatures';
import { TimeSheetStatusBadge } from '@/components/timesheet/TimeSheetStatusBadge';
import { Button } from '@/components/ui/button';
import { useListControls } from '@/hooks/useListControls';
import { useRelatedByIds } from '@/hooks/useRelatedByIds';
import { usePagedList } from '@/hooks/usePagedList';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import { buildRequestListRequest, DEFAULT_REQUEST_FILTERS } from './requestListQuery';
import type { ResolvablePath } from '@/models/resolve';
import { TIME_SHEET_SUMMARIES } from '@/models/entities/TimeSheetSummaryModel';

/** Période et équipe des demandes (l'employé est l'utilisateur connecté) */
const RESOLVABLES: ResolvablePath[] = [['period'], ['teamMember', 'team']];

/**
 * Mes demandes (§4.8) : les feuilles d'heures de l'employé dans la société sélectionnée, de la plus récente période à
 * la plus ancienne, brouillon du mois en cours compris (lien vers la Feuille d'heure), paginées côté API, filtrables par
 * statut. Une demande ouvre son détail (/requests/{id}) ; le PDF viendra plus tard. Une absence que l'employé n'a pas
 * encore signée le signale (tag, bouton « Signer » vers le détail).
 */
export function RequestsPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const list = useListControls({ filters: DEFAULT_REQUEST_FILTERS, resetKey: company?.id });
  const { filters, page, itemsPerPage, changeFilters } = list;
  const statuses = useReferenceOptions('timeSheetStatuses');
  const request = useMemo(
    () =>
      buildRequestListRequest(company?.memberId ?? null, filters, page, itemsPerPage, (reference) =>
        findReferenceId(statuses, reference),
      ),
    [company?.memberId, filters, page, itemsPerPage, statuses],
  );
  // Statut et type des demandes : lus par leur id dans les listes de référence (chargées une fois par session)
  useReferences('timeSheetStatuses', 'timeSheetTypes');
  const {
    models: sheets,
    totalItems,
    loading,
    error,
  } = usePagedList(TimeSheetModel, request, 0, {
    resolvables: RESOLVABLES,
    summary: TIME_SHEET_SUMMARIES,
  });
  // Signatures des absences seulement : celles à signer par l'employé
  const signatures = useRelatedByIds(
    useMemo(() => sheets.filter((sheet) => sheet.isAbsence).map((sheet) => Number(sheet.id)), [sheets]),
    loadSignatures,
  );

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }

  return (
    <>
      {/* Absences à signer, mises en avant au-dessus de la liste */}
      <AbsencesToSignBox />
      <Panel className="overflow-hidden">
        <ListToolbar search={<div className="flex-1" />}>
          <FilterSelect
            label="Filtrer par statut"
            allLabel="Tous les statuts"
            value={filters.timeSheetStatus}
            onChange={(timeSheetStatus) => changeFilters({ timeSheetStatus })}
          >
            <ReferenceOptions name="timeSheetStatuses" />
          </FilterSelect>
        </ListToolbar>

        <div className="overflow-x-auto">
          <table
            className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
            aria-busy={loading}
          >
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-4">Période</th>
                <th className="px-6 py-4">Soumise le</th>
                <th className="px-6 py-4">Total heures</th>
                <th className="px-6 py-4">Équipe</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sheets.map((sheet) => {
                const toSign = sheet.isAbsence && !!signatures && !signatures.get(Number(sheet.id))?.employee;
                return (
                  <tr key={sheet.id} className="hover:bg-slate-50/80 transition-colors" data-testid="request-row">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      <Link to={`/requests/${sheet.id}`} className="hover:text-blue-700 hover:underline">
                        {sheet.period.monthLabel}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                      {sheet.isDraft ? (
                        <span className="text-slate-400">Pas encore soumise</span>
                      ) : (
                        // Absence : date de sa déclaration (absences antérieures à cette règle : sans date)
                        (sheet.submittedAt?.format(DATE_TIME_FORMAT) ??
                        (sheet.isAbsence && <span className="text-slate-400">Absence déclarée</span>))
                      )}
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {sheet.isAbsence ? (
                        <AbsenceBadge />
                      ) : sheet.totalMinutes || sheet.isDraft ? (
                        formatMinutes(sheet.totalMinutes)
                      ) : (
                        <span className="font-normal text-slate-400 italic">0h (aucune heure)</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <TeamTag name={sheet.teamName} color={sheet.teamColor} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <TimeSheetStatusBadge
                          reference={sheet.timeSheetStatusReference}
                          label={sheet.timeSheetStatusLabel}
                        />
                        {toSign && <PendingSignatureTag />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {sheet.isDraft ? (
                        <Button asChild variant="outline" size="sm">
                          <Link to="/timesheet">
                            <PenLine />
                            Compléter ma feuille
                          </Link>
                        </Button>
                      ) : toSign ? (
                        <Button asChild size="sm">
                          <Link
                            to={`/requests/${sheet.id}`}
                            aria-label={`Signer l'absence de ${sheet.period.monthLabel}`}
                          >
                            <Signature />
                            Signer
                          </Link>
                        </Button>
                      ) : (
                        <Link
                          to={`/requests/${sheet.id}`}
                          aria-label={`Voir la demande de ${sheet.period.monthLabel}`}
                          title="Voir la demande"
                          className="inline-flex p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Eye className="w-5 h-5" />
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ListStatus
            className="p-6"
            loading={!companiesLoaded || (loading && sheets.length === 0)}
            error={error}
            empty={
              !loading &&
              sheets.length === 0 &&
              (filters.timeSheetStatus ? 'Aucune demande avec ce statut.' : "Vous n'avez encore aucune demande.")
            }
          />
        </div>

        <Pagination {...list.pagination} totalItems={totalItems} label={['demande', 'demandes']} feminine />
      </Panel>
    </>
  );
}
